/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Box, Typography } from "@material-ui/core";
import {
  DsmButton,
  DsmEmptyState,
  DsmGrid,
  DsmIcon,
} from "@dsm-dcs/design-system-react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import DatasetListTableV2 from "./DatasetListTableV2";
import RoiConfirmDialog from "./RoiConfirmDialog";
import Can from "../../../modules/Auth/Can";
import { DatasetType, FormType, isReadOnlyAnimalSystem } from "./common";
import featureFlags from "../../utils/featureFlags";
import { fetchRoiCalculationsByBaseline } from "../../apicalls/roiFunctions";
import {
  isPoultry,
  isBeef,
  isShrimp,
  isDairy,
  isPig,
} from "../../helpers/animals";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { prodProcessDatasetsPageStyles } from "../../../ThemeProvider";
import WarningDialog from "../../../sustell_15/components/helpers/WarningDialog";

interface InterventionSectionProps {
  prodProcess: any;
  activeBaseline: any;
  benchmark: boolean;
  // Threaded to DatasetListTableV2; interventions stay editable regardless.
  partnerReadOnly?: boolean;
  customerID: string;
  farmID: string;
  processID: string;
  selectedFarmName: string;
  openDataset: (
    datasetType: string,
    baselineId: string,
    formType: string,
    reference?: string,
  ) => void;
  makeDeleteHandler: (type: string) => (itemName: string) => void;
  handleDuplicate: (datasetType: string, item: any) => void;
}

const InterventionSection = ({
  prodProcess,
  activeBaseline,
  benchmark,
  partnerReadOnly = false,
  customerID,
  farmID,
  processID,
  selectedFarmName,
  openDataset,
  makeDeleteHandler,
  handleDuplicate,
}: InterventionSectionProps) => {
  const intl = useIntl();
  const history = useHistory();
  const pageClasses = prodProcessDatasetsPageStyles();

  const [roiConfirmModal, setRoiConfirmModal] = useState<any>(null);
  const [openComingSoonDialog, setOpenComingSoonDialog] = useState(false);

  const isRoiModalOpen = roiConfirmModal !== null;
  const shouldShowRoi =
    (featureFlags.roiCalculatorPoultry && isPoultry(prodProcess?.animalType)) ||
    (featureFlags.roiCalculatorPig && isPig(prodProcess?.animalType));

  // ROI status is read from the RoiCalculationTable (persistence-model access
  // pattern 1) and joined onto the interventions in memory by
  // interventionReference — never denormalized onto the InterventionTable.
  const baselineReference =
    activeBaseline?.reference ?? activeBaseline?.interventions?.[0]?.baseline;
  const [roiByReference, setRoiByReference] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!shouldShowRoi || !customerID || !baselineReference) {
      setRoiByReference({});
      return undefined;
    }
    let cancelled = false;
    fetchRoiCalculationsByBaseline({ customerId: customerID, baselineReference })
      .then((summaries) => {
        if (cancelled) return;
        const map: Record<string, any> = {};
        summaries.forEach((summary) => {
          map[summary.interventionReference] = {
            referenceId: summary.referenceId,
            value: summary.value,
            currency: summary.currency,
          };
        });
        setRoiByReference(map);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load ROI calculations for baseline:", err);
        if (!cancelled) setRoiByReference({});
      });
    return () => {
      cancelled = true;
    };
  }, [shouldShowRoi, customerID, baselineReference]);

  const interventionsWithRoi = (activeBaseline?.interventions ?? []).map(
    (intervention: any) => ({
      ...intervention,
      roi: roiByReference[intervention.reference] ?? null,
    }),
  );

  const handleAddInterventionWithComingSoon = () => {
    const isComingSoon =
      (isShrimp(prodProcess.animalType) && !featureFlags.shrimpInterventions) ||
      (isBeef(prodProcess.animalType) && !featureFlags.beefInterventions) ||
      (isDairy(prodProcess.animalType) && !featureFlags.dairyInterventions);

    if (isComingSoon) {
      setOpenComingSoonDialog(true);
      return;
    }

    openDataset(
      DatasetType.INTERVENTION,
      activeBaseline?.id,
      FormType.Add,
      "NEW",
    );
  };

  const closeRoiConfirmModal = () => {
    setRoiConfirmModal(null);
  };

  const confirmRoiFlow = () => {
    roiConfirmModal.proceed?.();
    closeRoiConfirmModal();
  };

  const hasRoiCalculation = (item: any) =>
    item?.roi?.value !== null && item?.roi?.value !== undefined;

  const handleEditIntervention = (item: any, proceed: any) => {
    if (hasRoiCalculation(item)) {
      setRoiConfirmModal({ mode: "edit", proceed });
      return;
    }
    proceed();
  };

  const handleInterventionRoiClick = (item: any) => {
    const roiPath = `/customers/${customerID}/farm/${farmID}/pp/${processID}/roi/${item.reference}`;

    history.push({
      pathname: hasRoiCalculation(item) ? `${roiPath}/results` : roiPath,
      state: {
        baselineName: activeBaseline?.name,
        interventionName: item?.name,
        farmName: selectedFarmName,
        processName: prodProcess?.name,
        animalType: prodProcess?.animalType,
      },
    });
  };

  const handleDuplicateWithRoiCheck = (item: any) => {
    const proceed = () => {
      handleDuplicate(DatasetType.INTERVENTION, item);
    };

    const hasRoi = item?.roi?.value !== null && item?.roi?.value !== undefined;
    if (hasRoi) {
      setRoiConfirmModal({ mode: "duplicate", proceed });
      return;
    }

    proceed();
  };

  return (
    <>
      <RoiConfirmDialog
        open={isRoiModalOpen}
        mode={roiConfirmModal?.mode}
        onClose={closeRoiConfirmModal}
        onConfirm={confirmRoiFlow}
      />

      <WarningDialog
        isOpen={openComingSoonDialog}
        closeModal={() => setOpenComingSoonDialog(false)}
        title="INTERVENTIONS.COMING_SOON.DIALOG.TITLE"
        body="INTERVENTIONS.COMING_SOON.DIALOG.BODY"
      />

      <DsmGrid
        className={`${pageClasses.sectionGrid} ${pageClasses.interventionsGridTemplate}`}
      >
        <Box className={pageClasses.alignSelfStart}>
          <Typography
            variant="h6"
            className={pageClasses.datasetsTableIntroTitle}
          >
            {intl.formatMessage({
              id: "SUSTELL.CURRENT.BASELINE.MODEL.INTERVENTION",
            })}
          </Typography>

          <Typography
            variant="body1"
            className={pageClasses.bodyTextNeutralDarkerWithMargin}
          >
            {intl.formatMessage(
              {
                id: "SUSTELL.CURRENT.BASELINE.INTERVENTION.DESCRIPTION",
              },
              {
                br: <br />,
              },
            )}
          </Typography>
        </Box>

        {Can("create", "Intervention") &&
          !benchmark &&
          !isReadOnlyAnimalSystem(prodProcess.animalType) && (
            <DsmButton
              className={pageClasses.addInterventionButton}
              onClick={handleAddInterventionWithComingSoon}
              variant="secondary"
            >
              <DsmIcon slot="before" name="general/plus" />
              {intl.formatMessage({ id: "SUSTELL.ADD_INTERVENTION" })}
            </DsmButton>
          )}
      </DsmGrid>

      {activeBaseline?.interventions?.length > 0 ? (
        <DatasetListTableV2
          datasets={interventionsWithRoi}
          customerID={customerID}
          variant="intervention"
          farmID={farmID}
          processID={prodProcess?.id}
          farmName={selectedFarmName}
          processName={prodProcess?.name}
          animalType={prodProcess.animalType}
          archive={false}
          benchmark={benchmark}
          partnerReadOnly={partnerReadOnly}
          openDataset={(baselineId, formType, reference) =>
            openDataset(
              DatasetType.INTERVENTION,
              baselineId,
              formType,
              reference,
            )
          }
          deleteHandler={makeDeleteHandler(DatasetType.INTERVENTION)}
          duplicateDataset={
            shouldShowRoi
              ? handleDuplicateWithRoiCheck
              : (item) => handleDuplicate(DatasetType.INTERVENTION, item)
          }
          activeBaselineYear={activeBaseline.year}
          showRoiColumn={shouldShowRoi}
          onRoiAction={shouldShowRoi ? handleInterventionRoiClick : undefined}
          onEditIntervention={handleEditIntervention}
        />
      ) : (
        <DsmEmptyState
          icon="general/search-md"
          header={intl.formatMessage({ id: "GENERAL.NO_DATA" })}
          description={intl.formatMessage({
            id: "SUSTELL.PROCESS.CREATE.INTERVENTION",
          })}
          className={pageClasses.sectionEmptyState}
        />
      )}
    </>
  );
};

export default InterventionSection;
